import React, { useState, useEffect, useRef } from "react";
import { getChat, getSentiment, getLastCheckIn } from "./../Api";
import secureLocalStorage from "react-secure-storage";
import Modal from "react-modal";
import confetti from "canvas-confetti";
import { checkInPrompts, quoteMessage, typingSet } from "../services/constants";
import "../css/CheckIn.css";

const DailyJournal = (lastCheckDate, patientId) => {
  const [patient, setPatient] = useState(null);
  const [patientName, setPatientName] = useState(null);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [doneModalIsOpen, setDoneModalIsOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [typingMessage, setTypingMessage] = useState("");
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [lastCheckIn, setLastCheckIn] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [isPreventive, setIsPreventive] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState(currentPrompt);
  const chatBoxRef = useRef(null);
  const [forceRender, setForceRender] = useState(false);

  const openModal = () => {
    setIsOpen(true);
    if (!messageSent) {
      const randomPrompt = getRandomPrompt();
      setCurrentPrompt(randomPrompt);
      setInitialPrompt(randomPrompt);
    } else {
      setCurrentPrompt(initialPrompt);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const getRandomPrompt = () => {
    return checkInPrompts[Math.floor(Math.random() * checkInPrompts.length)];
  };

  const getRandomMessage = () =>
    quoteMessage[Math.floor(Math.random() * quoteMessage.length)];

  const [oPopupMessage, setPopupMessage] = useState(getRandomMessage);

  useEffect(() => {
    const patientInfo = secureLocalStorage.getItem("patient_info");

    if (patientInfo) {
      // Check if the content is valid JSON:
      let parsedPatientInfo;
      if (typeof patientInfo === "string") {
        if (
          patientInfo.trim().startsWith("{") ||
          patientInfo.trim().startsWith("[")
        ) {
          try {
            console.log("Raw patient info from storage:", patientInfo);
            parsedPatientInfo = JSON.parse(patientInfo);
          } catch (error) {
            console.error("Error parsing patient_info:", error);
            return;
          }
        } else {
          console.error(
            "patient_info is not valid JSON, possible HTML response:",
            patientInfo
          );
          return;
        }
      } else {
        parsedPatientInfo = patientInfo;
      }

      // Now it's safe to set the state
      setPatientName(parsedPatientInfo.patient_name);
      setStartDate(parsedPatientInfo.start_date);
      setIsPreventive(parsedPatientInfo.is_preventive);
      setPatient(parsedPatientInfo);
    }
  }, [patientId]);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [chatHistory]);

  // useEffect(() => {
  //   const fetchLastCheckIn = async () => {
  //     try {
  //       const response = await getLastCheckIn(patientId.patientId);
  //       if (response) {
  //         const lastCheckInData = JSON.parse(response);
  //         const lastCheckInDate = new Date(lastCheckInData.patient_info.last_checkin_date);
  //         setLastCheckIn(lastCheckInDate.toDateString()); // Convert Date object to string in the format Wed Feb 19 2025
  //         console.log(lastCheckInDate.toDateString());
  //         console.log(new Date().toDateString());
  //       } else {
  //         console.error("No data returned from getLastCheckIn");
  //       }
  //     } catch (error) {
  //       console.error("Error fetching last check-in:", error);
  //     }
  //   };

  //   fetchLastCheckIn();
  // }, [patientId]);

  const handleSendMessage = async () => {
    const sendChatButton = document.querySelector(".sendChat");
    if (sendChatButton) {
      sendChatButton.style.pointerEvents = "none";
    }
    const skipButtonContainer = document.querySelector(".skip-btn-container");
    if (skipButtonContainer) {
      skipButtonContainer.style.display = "none";
    }
    if (chatInput.trim() === "") return;
    const userMessage = { role: "user", content: chatInput };
    const initialPrompt = { role: "ai", content: currentPrompt };
    setChatHistory((prev) => [...prev, userMessage]);
    setMessageSent(true);
    setInitialPrompt(currentPrompt);
    setIsTyping(true);
    const selectedSet = typingSet[Math.floor(Math.random() * typingSet.length)];
    let messageIndex = 0;
    const lastMessageIndex = selectedSet.length - 1;

    function updateLoadingMessage() {
      if (messageIndex < lastMessageIndex) {
        setTypingMessage(selectedSet[messageIndex]);
        messageIndex++;

        const randomInterval = Math.random() * 1500 + 3000;
        setTimeout(updateLoadingMessage, randomInterval);
      }
    }

    updateLoadingMessage();

    try {
      const sessionId = Math.random().toString(36).substring(2, 15);
      setSessionId(sessionId);

      const response = await getChat({
        topic: chatInput,
        patientId: secureLocalStorage.getItem("patient_id"),
        history: [initialPrompt, ...chatHistory, userMessage],
        personality: "NURSE_PERSONALITY",
        session_id: sessionId,
      });
      const janeMessage = {
        role: "ai",
        content: response.message.replace(/\n/g, "<br />"),
      };
      setChatHistory((prev) => [...prev, janeMessage]);
      document.querySelector(".chat-field").style.display = "none";
      document.querySelector(".sendChat").style.display = "none";
      document.querySelector(".button-container").style.display = "flex";
    } catch (error) {
      console.error("Error fetching chat response:", error);
    } finally {
      setIsTyping(false);
      setChatInput("");
    }
  };

  const handleSummary = async () => {
    try {
      const response = await getSentiment({
        chat_history: chatHistory,
        is_preventive: isPreventive,
        patient_id: secureLocalStorage.getItem("patient_id"),
        session_id: sessionId,
      });

      const modalContainer = document.querySelector(".checkin-modal");
      let sentimentData;

      modalContainer.innerHTML = `
        <div class="checkin-summary">
          <h3>Summary</h3>
          <p class="checkin-summary-content">${response.summary}</p>
          <h4>Moods</h4>
          <p class="highlight">${
            Array.isArray(response.mood)
              ? response.mood
                  .map(
                    (mood) => `<span class="checkin-highlight">${mood}</span>`
                  )
                  .join(", ")
              : response.mood
          }</p>
          <p></p>
          <h4>Topics</h4>
          <p class="highlight">${
            Array.isArray(response.sentiment)
              ? response.sentiment
                  .map(
                    (sentiment) =>
                      `<span class="checkin-highlight">${sentiment}</span>`
                  )
                  .join(", ")
              : response.sentiment
          }</p>
        </div>`;

      const doneButton = document.createElement("button");
      doneButton.className = "checkin-done secondary-button";
      doneButton.textContent = "Done for Today";
      doneButton.onclick = async () => {
        const checkinButton = document.querySelector(".journal-section button");
        if (checkinButton && !checkinButton.disabled) {
          checkinDone();
          try {
            const patientId = secureLocalStorage.getItem("patient_id");
            const response = await fetch(
              `https://api-k8s.engiv.io/checkin/${patientId}`,
              {
                method: "POST",
              }
            );
            if (!response.ok) {
              throw new Error("Network response was not ok");
            }
            console.log("Check-in recorded successfully");
          } catch (error) {
            console.error("Error recording check-in:", error);
          }
        }
      };
      modalContainer.appendChild(doneButton);
    } catch (error) {
      console.error("Error fetching sentiment analysis:", error);
    }
  };

  const handleKeepGoing = async () => {
    const buttonContainer = document.querySelector(".button-container");
    const sendChatButton = document.querySelector(".sendChat");
    const chatBox = document.querySelector(".chat-box");
    if (chatBox) {
      chatBox.style.overflowY = "auto";
    }
    const chatField = document.querySelector(".chat-field");

    if (buttonContainer) {
      buttonContainer.style.display = "none";
    }
    if (sendChatButton) {
      sendChatButton.style.display = "block";
    }
    if (chatField) {
      chatField.style.display = "block";
    }
  };

  const checkinDone = () => {
    closeModal();
    setDoneModalIsOpen(true);
    confetti();
    const checkinButton = document.querySelector(".journal-section button");
    if (checkinButton) {
      checkinButton.disabled = true;
      checkinButton.classList.add("disabled");
      checkinButton.textContent = "Done";
    }
  };

  const today = new Date().toDateString();
  console.log(today);
  console.log(lastCheckDate.lastCheckDate);
  console.log("StartDate:", startDate);
  return (
    <section className="journal-section">
      <span className="tag">Daily Journal</span>
      <div className="journal-section-container">
        <div className="left">
          <h3>How are you today, {patientName || ""}?</h3>
          <p>
            Take a moment to check-in with yourself. Your thoughts and emotions
            matter too.
          </p>
        </div>
        <div className="right">
          <img src="../journalLogo.svg" width="97" alt="Journal Logo" />
          <button
            onClick={openModal}
            disabled={lastCheckDate.lastCheckDate === today}
            className={lastCheckDate.lastCheckDate === today ? "disabled" : ""}
          >
            <img src="../down_arrow.svg" width="10" alt="Down Arrow" />
            {lastCheckDate.lastCheckDate === today ? "Done" : "Check in now"}
          </button>
        </div>
      </div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Daily Journal Modal"
        className="journal-modal"
      >
        <div className="modal-container checkin-modal">
          <div className="checkin-nav">
            <div
              className="back-btn-container flex gap-10"
              onClick={() => {
                closeModal();
                if (!messageSent) {
                  setCurrentPrompt(getRandomPrompt());
                } else {
                  // Restore the initial prompt if Back is clicked after sending a message
                  setCurrentPrompt(initialPrompt);
                }
              }}
            >
              <img src="../backButton.svg" alt="back" className="back-btn" />{" "}
              Back
            </div>

            {!messageSent && (
              <div
                className="skip-btn-container flex gap-10"
                onClick={() => {
                  setCurrentPrompt(getRandomPrompt());
                }}
              >
                Skip this one
              </div>
            )}
          </div>
          <div className="modal-container-title heading fade1">
            <h3 className="typing-effect">{currentPrompt}</h3>
          </div>
          <div className="checkin-field chat-with-jane">
            <input
              type="text"
              className="chat-field"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
              disabled={isTyping}
            />
            <a
              className={`sendChat ${!chatInput ? "disabled" : ""}`}
              onClick={chatInput ? handleSendMessage : null}
              style={{
                pointerEvents: chatInput ? "auto" : "none",
                opacity: chatInput ? 1 : 0.5,
              }}
            >
              <img src="../sendChat.svg" alt="Send" />
            </a>
          </div>
          <div className="chat-box" ref={chatBoxRef}>
            {chatHistory.map((message, index) => (
              <div
                key={index}
                className={`chat-bubble ${
                  message.role === "user" ? "user-bubble" : "jane-bubble"
                }`}
                style={{
                  animation: "slideUp 0.5s ease-out",
                  transformOrigin:
                    message.role === "user" ? "bottom right" : "bottom left",
                }}
              >
                <div>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: message.content.replace(
                        /\*\*(.*?)\*\*/g,
                        "<b>$1</b>"
                      ),
                    }}
                  />
                </div>
              </div>
            ))}

            {isTyping && (
              <div
                className="chat-bubble jane-bubble typing-indicator"
                style={{
                  animation: "slideUp 0.5s ease-out",
                  transformOrigin: "bottom left",
                }}
              >
                <p>{typingMessage}</p>
              </div>
            )}

            <div className="button-container" style={{ display: "none" }}>
              <button className="primary-button" onClick={handleKeepGoing}>
                Keep Going
              </button>
              <button className="secondary-button" onClick={handleSummary}>
                Done for Today
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={doneModalIsOpen}
        onRequestClose={() => setDoneModalIsOpen(false)}
        contentLabel="Onboarding Modal"
        className="onboarding-modal fade1"
      >
        <h2>
          Day{" "}
          {startDate
            ? Math.abs(
                Math.floor(
                  (Date.now() - new Date(startDate).getTime()) /
                    (1000 * 60 * 60 * 24)
                )
              ) + 1
            : "N/A"}
        </h2>
        <div className="quoted-text">
          <p>{oPopupMessage}</p>
        </div>
        <div className="centered-texts">
          <a className="engivio-text" onClick={() => {}}>
            www.engivio.com
          </a>
          {/* <a className="return" onClick={() => setIsOpen(false)}></a> */}
          <a className="return" href="/">
            Return to Home
          </a>
        </div>
        {/* <button onClick={() => setIsOpen(false)}>Close</button> */}
      </Modal>
    </section>
  );
};

export default DailyJournal;
